import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import type { RecorderCallbacks, SummaryResult } from '@chewy/audio-to-notes-ui'
import { v4 as uuid } from 'uuid'

import SnackNotificationType from '~/constants/SnackNotificationType'
import {
  getRecordingSoapId,
  setInitializingSession,
  setRecording,
  setRecordingSoapId,
  setSummarizing,
} from '~/store/duck/audioRecorder'
import { setPendingInjection } from '~/store/duck/audioRecorderInjection'
import { addUiNotification } from '~/store/duck/uiNotifications'
import { getPatientName } from '~/store/reducers/patients'
import {
  getDischargeNotes,
  getMedicalNotes,
  getPatientId,
  getSoapId,
} from '~/store/reducers/soap'
import { determineSummaryDestination } from '~/utils/summaryHandling'

import { useSummaryContext } from './useSummaryContext'

export interface AudioRecorderModalHandlers {
  onOpenMicPermissionModal: () => void
  onOpenNoAudioModal: (callbacks: {
    onContinueRecording?: () => void
    onEndRecording?: () => void
  }) => void
}

export const useAudioRecorderCallbacks = (
  modalHandlers: AudioRecorderModalHandlers,
): RecorderCallbacks => {
  const dispatch = useDispatch()
  const store = useStore()
  const { t } = useTranslation(['Common', 'Errors'])
  const context = useSummaryContext()
  const medicalNotes = useSelector(getMedicalNotes)
  const dischargeNotes = useSelector(getDischargeNotes)
  const patientId = useSelector(getPatientId)
  const patientName = useSelector(getPatientName(patientId))
  const recordingSoapId = useSelector(getRecordingSoapId)
  // Read soapId directly from Redux inside the hook to ensure we always have the latest value
  // This avoids closure issues where context.soapId might be stale when recording starts
  const currentSoapId = useSelector(getSoapId)
  // Note: isRecording is available via callback parameters (data.isRecording), not needed from Redux here

  // Set recordingSoapId on SOAP page mount (for "Summarize" without recording)
  useEffect(() => {
    if (context.isOnSoapPage && currentSoapId && !recordingSoapId) {
      dispatch(setRecordingSoapId(currentSoapId))
    }
  }, [context.isOnSoapPage, currentSoapId, recordingSoapId, dispatch])

  // onBeforeSummarize
  const handleBeforeSummarize = useCallback(
    async (_params: {
      appName: string
      correlationId: string
      templates?: Record<string, string>
    }) =>
      // Return existing notes for context in transcript field
      [
        {
          summary: {
            type: 'soap' as const,
          },
          transcript: { text: medicalNotes || '' },
        },
        {
          summary: {
            type: 'discharge' as const,
          },
          transcript: { text: dischargeNotes || '' },
        },
      ],
    [medicalNotes, dischargeNotes],
  )

  // State tracking callbacks
  const handleRecordingStateChange = useCallback(
    (data: {
      appName: string
      correlationId: string
      isRecording: boolean
      recordId: string | null
    }) => {
      try {
        // Always update state - callbacks are source of truth
        // Redux state reflects actual recording state, not current page context
        dispatch(setRecording(data.isRecording))

        // Capture soapId when recording starts - this locks it to the SOAP where recording began
        // This ensures mutation updates the correct SOAP even if user navigates away
        if (data.isRecording && context.soapId) {
          dispatch(setRecordingSoapId(context.soapId))
        } else if (!data.isRecording) {
          // Clear recordingSoapId when recording stops (but keep it during summarization)
          // We'll clear it in onSessionChanged when session fully ends
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onRecordingStateChange', error)
        // Still update state to prevent stuck state
        dispatch(setRecording(false))
      }
    },
    [dispatch, context.soapId],
  )

  const handleSummarizationStateChange = useCallback(
    (data: {
      appName: string
      correlationId: string
      isSummarizing: boolean
      recordId: string | null
    }) => {
      try {
        dispatch(setSummarizing(data.isSummarizing))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSummarizationStateChange', error)
        dispatch(setSummarizing(false))
      }
    },
    [dispatch],
  )

  // onSessionChanged
  const handleSessionChanged = useCallback(
    (_data: {
      appName: string
      newCorrelationId: string
      newSessionId: string | null
      oldCorrelationId: string
      oldSessionId: string | null
    }) => {
      try {
        // Reset state when session changes (different appointment/correlationId)
        dispatch(setRecording(false))
        dispatch(setSummarizing(false))
        dispatch(setInitializingSession(false))
        dispatch(setRecordingSoapId(null))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSessionChanged', error)
        // Still reset state to prevent stuck state
        dispatch(setRecording(false))
        dispatch(setSummarizing(false))
        dispatch(setInitializingSession(false))
        dispatch(setRecordingSoapId(null))
      }
    },
    [dispatch],
  )

  // onCorrelationIdChange
  const handleCorrelationIdChange = useCallback(
    (data: {
      appName: string
      currentState: string
      isRecording: boolean
      isSummarizing: boolean
      newCorrelationId: string
      oldCorrelationId: string
      recordId: string | null
    }) => {
      // correlationId stays fixed during active states - this should not fire
      // eslint-disable-next-line no-console
      console.error('Unexpected correlationId change', data)
    },
    [],
  )

  // onComponentUnmount
  const handleComponentUnmount = useCallback(
    (_data: {
      appName: string
      correlationId: string
      hasActiveRecording: boolean
      hasActiveSummarization: boolean
      lifecycleState: string
      recordId: string | null
    }) => {
      // Reset initialization state on unmount
      dispatch(setInitializingSession(false))
      // Optional: Cleanup Portal-specific state
      // Library handles its own cleanup via config
    },
    [dispatch],
  )

  // onSummarizationStart
  const handleSummarizationStart = useCallback(
    (_data: {
      appName: string
      correlationId: string
      recordId: string | null
    }) => {
      // No-op: Previously captured patient name for mismatch modal, but modals removed
    },
    [],
  )

  // onSessionInit
  const handleSessionInit = useCallback(
    (_data: {
      appName: string
      correlationId: string
      hasRecording: boolean
      hasSummary: boolean
    }) => {
      // No-op: recordingSoapId is now set in onRecordingStateChange when recording starts
      // This prevents recordingSoapId from being overwritten when user navigates to different SOAP
    },
    [],
  )

  // onSummarizationComplete
  // Dispatches pending injection to Redux — CKEditor's Model API (via useAudioSummaryInjection)
  // is the single write path for SOAP note persistence through the collaboration layer.
  // No GraphQL mutation needed; CKEditor onChange → collaboration sync handles backend writes.
  const handleSummarizationComplete = useCallback(
    (data: {
      appName: string
      correlationId: string
      recordId: string | null
      summaries: SummaryResult[]
    }) => {
      try {
        dispatch(setSummarizing(false))

        const { summaries, correlationId } = data

        const destination = determineSummaryDestination(summaries, {
          pathname: context.pathname,
          currentAppointmentId: context.currentAppointmentId ?? null,
          isOnSoapPage: context.isOnSoapPage,
          summariesFTEnabled: context.summariesFTEnabled,
          correlationId,
        })

        if (destination.type === 'discard') return

        if (destination.type === 'queued' || destination.type === 'inject') {
          const capturedSoapId = getRecordingSoapId(store.getState())

          const medicalSummary = destination.summaries.find(
            (s: SummaryResult) => s.summaryType === 'soap',
          )
          const dischargeSummary = destination.summaries.find(
            (s: SummaryResult) => s.summaryType === 'discharge',
          )
          const medicalNotesForInjection = medicalSummary?.summaryText || null
          const dischargeNotesForInjection =
            dischargeSummary?.summaryText || null

          const hasContent =
            medicalNotesForInjection || dischargeNotesForInjection

          if (!capturedSoapId || !hasContent) {
            // eslint-disable-next-line no-console
            console.warn(
              '[AudioRecorder] Skipping injection — missing soapId or empty summaries',
              {
                capturedSoapId,
                medicalNotesForInjection,
                dischargeNotesForInjection,
              },
            )
            return
          }

          dispatch(
            setPendingInjection({
              soapId: capturedSoapId,
              medicalNotes: medicalNotesForInjection,
              dischargeNotes: dischargeNotesForInjection,
            }),
          )

          const isOnPage = destination.type === 'inject'
          const toastMessage = isOnPage
            ? t('Common:AUDIO_RECORDER.NOTES_UPDATED_SUCCESSFULLY')
            : t('Common:AUDIO_RECORDER.SUMMARY_COMPLETED')
          const toastType = isOnPage
            ? SnackNotificationType.AUDIO_RECORDER_NOTES_UPDATED
            : SnackNotificationType.AUDIO_RECORDER_SUMMARY_COMPLETED
          dispatch(
            addUiNotification({
              id: uuid(),
              message: toastMessage,
              type: toastType,
              params: {
                soapId: capturedSoapId,
              },
            }),
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSummarizationComplete', error)
        dispatch(setSummarizing(false))
        dispatch(
          addUiNotification({
            id: uuid(),
            message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'),
            type: SnackNotificationType.ERROR,
          }),
        )
      }
    },
    [dispatch, t, context, store],
  )

  // getLockHolderName
  const getLockHolderName = useCallback(
    () =>
      // Return patient name to display in locked tabs ("Recording [name]...")
      patientName || undefined,
    [patientName],
  )

  // Modal callbacks
  const handleMicrophoneError = useCallback(
    (data: {
      appName: string
      correlationId: string
      error: Error
      errorCode: string
      recordId: string | null
    }) => {
      if (data.errorCode === 'PERMISSION_DENIED') {
        // Modal shows instructions on how to enable microphone access
        // Browser security prevents programmatically opening device settings
        modalHandlers.onOpenMicPermissionModal()
      }
    },
    [modalHandlers],
  )

  const handleNoAudioReceived = useCallback(
    (_data: {
      appName: string
      correlationId: string
      recordId: string | null
    }) => {
      modalHandlers.onOpenNoAudioModal({
        onContinueRecording: () => {
          // Intentionally empty - modal dismisses via onClose()
          // User can continue recording via library's AudioController UI
          // The library handles recording state internally
        },
        onEndRecording: () => {
          // TODO(SCRIBE-144): Implement stop recording without summarizing
          // Currently no way to programmatically stop recording from Portal callbacks
          // The library's AudioController handles recording control via its own UI
          // For now, modal dismisses via onClose() and user must stop via library UI
        },
      })
    },
    [modalHandlers],
  )

  // onError - Show "Doggo" error notification when status API or other errors occur
  const handleError = useCallback(
    (data: {
      appName: string
      correlationId: string
      error: Error
      recordId: string | null
    }) => {
      // eslint-disable-next-line no-console
      console.error('Audio Recorder Error:', {
        message: data.error.message,
        appName: data.appName,
        correlationId: data.correlationId,
        recordId: data.recordId,
      })

      // Show "Doggo" error notification (generic error for status API failures, etc.)
      dispatch(
        addUiNotification({
          id: uuid(),
          message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'), // TODO: Add specific ERROR_GENERIC translation key
          type: SnackNotificationType.ERROR,
        }),
      )
    },
    [dispatch, t],
  )

  return {
    onBeforeSummarize: handleBeforeSummarize,
    onRecordingStateChange: handleRecordingStateChange,
    onSummarizationStateChange: handleSummarizationStateChange,
    onSessionChanged: handleSessionChanged,
    onCorrelationIdChange: handleCorrelationIdChange,
    onComponentUnmount: handleComponentUnmount,
    onSessionInit: handleSessionInit,
    onSummarizationStart: handleSummarizationStart,
    onSummarizationComplete: handleSummarizationComplete,
    getLockHolderName,
    onMicrophoneError: handleMicrophoneError,
    onNoAudioReceived: handleNoAudioReceived,
    onError: handleError,
    // Note: Add onRecordingTimeout when library supports it
  }
}
